import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { Chip } from '@/components/Chip'
import { LauncherCard } from '@/components/LauncherCard'
import type { IconName } from '@/components/icons/iconData'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestoreObjectCatalogue = components['schemas']['RestoreObjectCatalogue']
type RestoreKind = components['schemas']['RestoreKind']

/** No served label exists for a KIND (only for each of its objects) — `RestoreKind` is a closed,
 * 8-member enum stable since §18.3, so a client-side display name is the same class of mapping
 * `ROW_ICON`/`TRIGGER_LABEL` already use elsewhere for other closed enums, not a second authority
 * over restorability (that stays server-served, per object, below). */
const KIND_LABEL: Record<RestoreKind, string> = {
  REPOSITORY: 'Repository Restore',
  REF: 'Branch and Tag Recovery',
  SETTINGS_POLICY: 'Settings and Policy Restore',
  ACCESS_INTEGRATION: 'Access and Integration Restore',
  CI_DEPLOYMENT: 'CI and Deployment Restore',
  REVIEW_STATE: 'Review State Restore',
  PROJECT_POLICY: 'Project Policy Restore',
  WORKSPACE_WEBHOOK: 'Workspace Webhook Restore',
}

/** `launcher-card`'s icon per kind — Prototype_v2.html `#v-restore`'s own `card g-blue` for each
 * of the eight (`i-repo`/`i-branch`/`i-shield`/`i-key`/`i-coin`/`i-pr`/`i-box`/`i-hook`), added
 * W174 alongside the component itself; every kind is `g-blue`, so there is no per-kind variant. */
const KIND_ICON: Record<RestoreKind, IconName> = {
  REPOSITORY: 'i-repo',
  REF: 'i-branch',
  SETTINGS_POLICY: 'i-shield',
  ACCESS_INTEGRATION: 'i-key',
  CI_DEPLOYMENT: 'i-coin',
  REVIEW_STATE: 'i-pr',
  PROJECT_POLICY: 'i-box',
  WORKSPACE_WEBHOOK: 'i-hook',
}

const KIND_WIZARD_ENTRY: Record<RestoreKind, string> = {
  REPOSITORY: 'repo-wz-1',
  REF: 'ref-wz-1',
  SETTINGS_POLICY: 'settings-wz-1',
  ACCESS_INTEGRATION: 'access-wz-1',
  CI_DEPLOYMENT: 'ci-wz-1',
  REVIEW_STATE: 'review-wz-1',
  PROJECT_POLICY: 'project-wz-1',
  WORKSPACE_WEBHOOK: 'wshook-wz-1',
}

/** The prototype's own three `section-heading`s over its `.grid3`s (Prototype_v2.html `#v-restore`)
 * — a client-side grouping of the same closed 8-member enum `KIND_LABEL`/`KIND_ICON` already map,
 * not a second catalogue: no operation serves a group for a kind, and none needs to, since which
 * group a `RestoreKind` belongs to never varies by tenant (W177). */
const KIND_GROUP: Record<RestoreKind, string> = {
  REPOSITORY: 'Repository and history recovery',
  REF: 'Repository and history recovery',
  SETTINGS_POLICY: 'Configuration and policy recovery',
  ACCESS_INTEGRATION: 'Configuration and policy recovery',
  CI_DEPLOYMENT: 'Configuration and policy recovery',
  REVIEW_STATE: 'Configuration and policy recovery',
  PROJECT_POLICY: 'Project and workspace recovery',
  WORKSPACE_WEBHOOK: 'Project and workspace recovery',
}
const GROUP_ORDER = ['Repository and history recovery', 'Configuration and policy recovery', 'Project and workspace recovery']

/** The prototype's own lead paragraph per kind (`Prototype_v2.html` `#v-restore`, each `card`'s
 * first `<p>`) — the same closed-enum client copy as `KIND_LABEL`, not served: `RestoreObjectCatalogue`
 * carries customer-facing copy per OBJECT (`label`/`recovers`/`prerequisite`/`fidelity`), never a
 * kind-level summary sentence (checked against the generated schema directly, W177). */
const KIND_LEAD: Record<RestoreKind, string> = {
  REPOSITORY: 'The repository itself is gone. Restore one, several or all of them: objects, refs, wiki and policy.',
  REF: 'Recreate a deleted branch or tag. No other ref is touched — the narrowest write this product makes.',
  SETTINGS_POLICY: 'Settings, the branching model and branch restrictions, after an admin error or a compromised session.',
  ACCESS_INTEGRATION: 'Deploy keys, webhooks, permissions and default reviewers, after a botched rotation or an uninstalled integration.',
  CI_DEPLOYMENT: 'Deployment environments, pipeline schedules, and pipeline and deployment variables.',
  REVIEW_STATE: 'Open pull requests, recreated with their title, description and branches. Both branches must exist first.',
  PROJECT_POLICY: "Project permissions, branching model, default reviewers and deploy keys — policy that inherits down to every repository inside it.",
  WORKSPACE_WEBHOOK: 'Workspace-scoped integrations, recreated where missing. Needs workspace-owner rights.',
}

/** The prototype's own SECOND paragraph per card (`Prototype_v2.html` `#v-restore`, each card's
 * `<p style="margin-top:7px">`) — present for 6 of the 8 kinds (`SETTINGS_POLICY`/`PROJECT_POLICY`
 * have none), same static, closed-enum shape as `KIND_LEAD`. Found missing entirely by the
 * semantic-diff harness's text comparison against the prototype. */
const KIND_DETAIL: Partial<Record<RestoreKind, string>> = {
  REPOSITORY:
    'Additive on content, by value on policy. A ref that already exists at the destination is not written — it is reported Refused, whatever its hash. Repository settings, the branching model and branch restrictions are written by value, and the destination’s prior value is recorded before it is replaced.',
  REF: 'Only where the ref is absent. A ref the destination already holds is Refused, whatever its hash and however long ago it moved. There is no force flag and no acknowledgement that produces one: recovering a branch that has since diverged means restoring beside it and reconciling by hand.',
  ACCESS_INTEGRATION: 'Webhook secrets are never returned. Hooks come back without theirs, listed for re-entry.',
  CI_DEPLOYMENT: 'Secured values are never readable — in pipeline and deployment scope alike. Every secured key is recreated empty and named individually for re-entry.',
  REVIEW_STATE: 'Approvals are not restored: the API would record them as yours.',
  WORKSPACE_WEBHOOK: 'Secrets are write-only here too, and listed for re-entry.',
}

/** `REF`'s own third paragraph, linking to Lost commits — the only kind with one. */
const REF_ROUTING_NOTE = 'Recovery from a detected history rewrite routes through here too, from Lost commits.'

/**
 * `v-restore` — process/09-SCREEN-SPECS/v-restore.md. `organisationGetRestoreObjectCatalogue`
 * is reference data, fetched rather than embedded (rule 1) — the write order and every object's
 * restorability come from `kinds[].objects[].order`/`not_restorable`, never a hardcoded array.
 *
 * **`link` added W166** — both the launcher card's own navigation anchor and the "See all restore
 * points" control were already real, working links; neither carried `data-cmp="link"`.
 *
 * **Grouped under three `section-heading`s and given each kind's own lead paragraph (W177)** — the
 * previous build rendered all eight kinds as one flat, ungrouped `grid3` with only an object-type
 * count, no descriptive copy at all. `KIND_GROUP`/`KIND_LEAD` are added alongside the three
 * existing closed-enum maps (`KIND_LABEL`/`KIND_ICON`/`KIND_WIZARD_ENTRY`) — same shape, not a new
 * kind of decision. `catalogue.data.kinds`' own order (never a hardcoded list, per this file's
 * long-standing rule) still decides which cards render; grouping only decides where an already-
 * present card is drawn, never invents or hides one.
 */
export function VRestore() {
  const orgId = useActiveOrgId()!
  const navigate = useNavigate()

  const catalogue = useQuery({
    queryKey: ['restore-object-catalogue', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async (): Promise<RestoreObjectCatalogue> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/reference/restore-objects', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RestoreObjectCatalogue
    },
  })

  const problem = catalogue.error instanceof ApiProblemError ? catalogue.error.problem : undefined

  return (
    <div data-scr={unitId('v-restore')}>
      <h1 className="m-0 mb-6 text-section font-semibold">Restore</h1>
      <Note>No restore ever deletes anything or overwrites code or history that carries commits nobody else holds.</Note>

      <div className="mt-4">
        <Strip>
          <b>Every restore starts from a restore point</b> — a backup that exists, paired with the newest configuration
          snapshot taken at or before it. You choose one inside each wizard, or{' '}
          <button
            type="button"
            data-cmp="link"
            data-act={actionId('nav.v-snapshots')}
            onClick={() => navigate('/snapshots')}
            className="underline text-link bg-transparent border-0 p-0 cursor-pointer"
          >
            browse all restore points
          </button>{' '}
          first to see how far back you can go.
        </Strip>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {GROUP_ORDER.map((group) => {
        const kinds = catalogue.data?.kinds.filter((k) => KIND_GROUP[k.kind] === group) ?? []
        if (kinds.length === 0) return null
        return (
          <section key={group}>
            <div data-cmp="section-heading" className="text-body-lg font-semibold mt-8 mb-3">
              {group}
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {kinds.map((k) => {
                const unrestorable = k.objects.filter((o) => o.not_restorable)
                return (
                  <LauncherCard
                    key={k.kind}
                    variant="blue"
                    icon={KIND_ICON[k.kind]}
                    title={KIND_LABEL[k.kind]}
                    actionId={actionId(`nav.${KIND_WIZARD_ENTRY[k.kind]}`)}
                    onClick={() => navigate(`/${KIND_WIZARD_ENTRY[k.kind]}`)}
                    description={
                      <>
                        <p className="m-0 mb-3">{KIND_LEAD[k.kind]}</p>
                        {KIND_DETAIL[k.kind] && <p className="m-0 mb-3">{KIND_DETAIL[k.kind]}</p>}
                        {k.kind === 'REF' && <p className="m-0 mb-3">{REF_ROUTING_NOTE}</p>}
                        <Chip variant="plain">{k.objects.length} object types</Chip>
                        {unrestorable.length > 0 && (
                          <div className="mt-3">
                            {unrestorable.map((o) => (
                              <Note key={o.object_type}>
                                {o.label}: {o.fidelity}
                              </Note>
                            ))}
                          </div>
                        )}
                      </>
                    }
                  />
                )
              })}
            </div>

            {group === 'Repository and history recovery' && (
              <>
                <div data-cmp="section-heading" className="text-small font-semibold mt-6 mb-1">
                  Included in the two restores above
                </div>
                <Note>
                  Neither of these is a restore use case of its own: there are eight, and each of these travels inside one
                  of them — LFS content inside Repository Restore, and lost commits inside Branch and Tag Recovery, which
                  pushes the pre-rewrite commit to a new branch. They are shown because a customer needs to know the
                  capability exists, not as a ninth and tenth thing to launch.
                </Note>
                <div className="grid gap-4 mt-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  <LauncherCard
                    variant="blue"
                    icon="i-box"
                    title="LFS Content Restore"
                    badge={<Chip variant="active">Verified end to end</Chip>}
                    description={
                      <>
                        <p className="m-0 mb-3">
                          The bytes behind committed pointers, held as content-addressed objects keyed by their own SHA-256
                          and fetched with the official <span className="font-mono">git-lfs</span> binary.
                        </p>
                        <p className="m-0">
                          Measured, both directions: the personal API token authenticates{' '}
                          <span className="font-mono">git-lfs</span>, and a test object came back byte-identical against its
                          own SHA-256. Platform limits still apply: 5 GB per file, and LFS content is excluded from
                          repository download archives.
                        </p>
                      </>
                    }
                  />
                  <LauncherCard
                    variant="blue"
                    icon="i-doc"
                    title="Lost Commit Recovery"
                    description={
                      <>
                        <p className="m-0 mb-3">
                          Commits made unreachable by a force push or rebase, found by comparing each backup with the one
                          before it.
                        </p>
                        <p className="m-0">
                          Restores to a new branch, and it runs as a Branch and Tag Recovery with everything already filled
                          in. Resetting the branch itself is not offered — a force push back would overwrite history, which
                          this product never does.
                        </p>
                      </>
                    }
                  />
                </div>
              </>
            )}

            {group === 'Project and workspace recovery' && (
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <LauncherCard
                  variant="off"
                  icon="i-users"
                  title="Members and Groups"
                  description={
                    <p className="m-0">
                      Captured, readable and reportable. No product can restore these: the endpoints are read-only, and
                      groups have no v2 API.
                    </p>
                  }
                />
              </div>
            )}
          </section>
        )
      })}

      <section>
        <div data-cmp="section-heading" className="text-body-lg font-semibold mt-8 mb-3">
          Future
        </div>
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <LauncherCard
            variant="off"
            disabled
            icon="i-file"
            title="File and Folder Recovery"
            description={
              <>
                <p className="m-0 mb-3">A file or directory removed by a commit. Readable and downloadable from the captured history today.</p>
                <p className="m-0">
                  Pending confirmation of Bitbucket’s commit-write support. A source-write endpoint appears to exist; until
                  it is verified this stays out of scope.
                </p>
              </>
            }
          />
          <LauncherCard
            variant="off"
            disabled
            icon="i-dl"
            title="Downloads and Artifacts"
            description={
              <>
                <p className="m-0 mb-3">Release binaries and signed artifacts, captured with checksums, held in the backup and restorable.</p>
                <p className="m-0">
                  Blocked on two things, and one of them is the customer's: the upload endpoint's request body is
                  undocumented, and the endpoint is plan-gated — it returns 402 on a Free workspace, which is a skip and
                  not a failure. Captured with a SHA-256 per file and held in the backup meanwhile.
                </p>
              </>
            }
          />
        </div>
      </section>

      <div className="mt-8">
        <Strip>
          <button
            type="button"
            data-cmp="link"
            data-act={actionId('nav.v-snapshots')}
            onClick={() => navigate('/snapshots')}
            className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-body-lg"
          >
            See all restore points
          </button>
        </Strip>
      </div>
    </div>
  )
}
